import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Link } from "react-router-dom";
import { Wallet, Zap, ShoppingCart, Banknote, ArrowUpRight, ArrowDownRight } from "lucide-react";

function fmt(n) {
    return new Intl.NumberFormat("en-KE", { maximumFractionDigits: 2 }).format(n || 0);
}

export default function Overview() {
    const [wallet, setWallet] = useState(null);
    const [summary, setSummary] = useState(null);
    const [recent, setRecent] = useState([]);

    useEffect(() => {
        (async () => {
            const [w, s, t] = await Promise.all([
                api.get("/wallet"),
                api.get("/analytics/summary"),
                api.get("/transactions?limit=6"),
            ]);
            setWallet(w.data);
            setSummary(s.data);
            setRecent(t.data);
        })();
    }, []);

    const stats = [
        { label: "Wallet balance", v: `KES ${fmt(wallet?.balance)}`, icon: Wallet, to: "/dashboard/wallet" },
        { label: "GMV (30d)", v: `KES ${fmt(summary?.gmv)}`, icon: ArrowUpRight, to: "/dashboard/analytics" },
        { label: "Commission earned", v: `KES ${fmt(summary?.commission_earned)}`, icon: ArrowDownRight, to: "/dashboard/analytics" },
        { label: "Sales", v: fmt(summary?.sales_count), icon: Zap, to: "/dashboard/sales" },
    ];

    return (
        <div className="space-y-8" data-testid="overview-page">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {stats.map((s, i) => {
                    const Icon = s.icon;
                    return (
                        <Link key={s.label} to={s.to} className={`og-card p-6 og-fade-up og-delay-${i + 1}`} data-testid={`stat-card-${i}`}>
                            <div className="flex items-center justify-between">
                                <p className="text-xs uppercase tracking-widest font-semibold text-slate-500">{s.label}</p>
                                <Icon size={18} className="text-onego-navy/60" />
                            </div>
                            <p className="mt-3 font-display text-3xl font-extrabold text-onego-navy">{s.v}</p>
                        </Link>
                    );
                })}
            </div>

            {/* Quick actions */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <Link to="/dashboard/sell" className="og-card p-7 relative overflow-hidden group" data-testid="quick-sell">
                    <div className="absolute -top-12 -right-10 h-40 w-40 rounded-full og-gradient opacity-20 group-hover:opacity-30 transition-opacity" />
                    <Zap className="text-pink-500" />
                    <h3 className="mt-4 font-display text-2xl font-bold text-onego-navy">Sell digital services</h3>
                    <p className="mt-1 text-slate-600">Airtime, data, electricity, TV, tickets &mdash; earn commission instantly.</p>
                </Link>
                <Link to="/dashboard/procurement" className="og-card p-7 relative overflow-hidden group" data-testid="quick-procure">
                    <div className="absolute -top-12 -right-10 h-40 w-40 rounded-full og-gradient opacity-20 group-hover:opacity-30 transition-opacity" />
                    <ShoppingCart className="text-cyan-500" />
                    <h3 className="mt-4 font-display text-2xl font-bold text-onego-navy">Order inventory</h3>
                    <p className="mt-1 text-slate-600">FMCG, beverages, household goods direct from suppliers.</p>
                </Link>
                <Link to="/dashboard/credit" className="og-card p-7 relative overflow-hidden group" data-testid="quick-credit">
                    <div className="absolute -top-12 -right-10 h-40 w-40 rounded-full og-gradient opacity-20 group-hover:opacity-30 transition-opacity" />
                    <Banknote className="text-amber-500" />
                    <h3 className="mt-4 font-display text-2xl font-bold text-onego-navy">Apply for credit</h3>
                    <p className="mt-1 text-slate-600">Float and inventory financing based on your transaction history.</p>
                </Link>
            </div>

            {/* Recent transactions */}
            <div className="og-card p-7">
                <div className="flex items-center justify-between">
                    <h3 className="font-display text-2xl font-bold text-onego-navy">Recent activity</h3>
                    <Link to="/dashboard/sales" className="og-chip" data-testid="recent-view-all">View all</Link>
                </div>
                <div className="mt-4 divide-y divide-slate-100">
                    {recent.length === 0 && (
                        <p className="py-6 text-slate-500 text-sm">No transactions yet. Top up your wallet and make your first sale.</p>
                    )}
                    {recent.map((t) => (
                        <div key={t.id} className="py-4 flex items-center justify-between gap-4">
                            <div>
                                <p className="font-bold text-onego-navy">{t.meta?.description || t.kind}</p>
                                <p className="text-xs text-slate-500">{new Date(t.created_at).toLocaleString()} &middot; Ref {t.ref}</p>
                            </div>
                            <div className="text-right">
                                <p className={`font-display font-bold ${t.kind === "TOPUP" || t.kind === "CREDIT_DISBURSE" ? "text-emerald-600" : "text-onego-navy"}`}>
                                    {t.kind === "TOPUP" || t.kind === "CREDIT_DISBURSE" ? "+" : "-"} KES {fmt(t.amount)}
                                </p>
                                <p className="text-xs uppercase tracking-widest text-slate-400">{t.status}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
